import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { portalPath } from "../lib/roles";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import { DemoVideo } from "../components/DemoVideo";
import { SRMark } from "../components/SRMark";
import {
  Stethoscope, Building2, UserRound, ShieldCheck, Gift, Search, KeyRound, BadgeCheck,
  ArrowRight, Check, CalendarClock, TrendingUp,
} from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/7108398/pexels-photo-7108398.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const Stat = ({ icon: Icon, title, sub }) => (
  <div className="flex items-start gap-3">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <p className="font-heading text-sm font-semibold text-slate-900">{title}</p>
      <p className="text-sm text-slate-500">{sub}</p>
    </div>
  </div>
);

const Step = ({ n, icon: Icon, title, body }) => (
  <div className="relative rounded-xl border border-slate-200 bg-white p-6">
    <span className="absolute right-5 top-5 font-heading text-3xl font-bold text-slate-100">{n}</span>
    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary text-primary-foreground">
      <Icon className="h-5 w-5" />
    </div>
    <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">{title}</h3>
    <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{body}</p>
  </div>
);

const AudienceCard = ({ icon: Icon, eyebrow, title, points, cta, ctaTo, video, videoTitle, testId, accent }) => (
  <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-7 transition-shadow hover:shadow-lg" data-testid={testId}>
    <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${accent}`}>
      <Icon className="h-6 w-6" />
    </div>
    <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-primary">{eyebrow}</p>
    <h3 className="mt-1 font-heading text-xl font-semibold text-slate-900">{title}</h3>
    <ul className="mt-4 space-y-2.5">
      {points.map((p) => (
        <li key={p} className="flex items-start gap-2 text-sm text-slate-600">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {p}
        </li>
      ))}
    </ul>
    <div className="mt-auto flex flex-col gap-2 pt-6">
      <Button asChild className="w-full" data-testid={`${testId}-cta`}>
        <Link to={ctaTo}>{cta} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
      </Button>
      <DemoVideo src={video} title={videoTitle} buttonLabel="Watch demo" variant="ghost" size="sm" className="w-full" testId={`${testId}-demo`} />
    </div>
  </div>
);

const PRICING_PLANS = [
  { id: "SOLO", name: "Individual", monthly: 39, sub: "1 practitioner" },
  { id: "CLINIC_LIGHT", name: "Clinic Light", monthly: 99, sub: "Up to 5 clinicians", featured: true },
  { id: "CLINIC_PLUS", name: "Clinic Plus", monthly: 189, sub: "Unlimited clinicians" },
];

const PricingTeaser = () => {
  return (
    <section id="pricing" className="mx-auto max-w-6xl px-5 py-14 sm:px-8">
      <div className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand">Pricing</p>
        <h2 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-slate-900">Free for GPs. Simple plans for everyone else.</h2>
        <p className="mt-3 text-sm font-semibold text-slate-700">Simple monthly plans, no annual commitment. 3 months free. Plans start from $39/month after trial. No card required.</p>
      </div>

      <div className="mt-8 grid gap-5 sm:grid-cols-3">
        {PRICING_PLANS.map((p) => (
          <div key={p.name} data-testid={`pricing-card-${p.id}`}
            className={`rounded-xl border p-7 ${p.featured ? "border-primary bg-white shadow-md ring-1 ring-primary/20" : "border-slate-200 bg-white"}`}>
            {p.featured && <span className="mb-3 inline-block rounded-md bg-secondary px-2 py-0.5 text-xs font-semibold text-secondary-foreground">Most popular</span>}
            <h3 className="font-heading text-lg font-semibold text-slate-900">{p.name}</h3>
            <div data-testid={`pricing-price-${p.id}`}>
              <p className="mt-2"><span className="font-heading text-3xl font-bold text-slate-900">${p.monthly}</span><span className="text-sm text-slate-400"> /mo AUD</span></p>
              <p className="mt-1 text-xs text-emerald-600">First 3 months free</p>
            </div>
            <p className="mt-3 text-sm text-slate-500">{p.sub}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default function Landing() {
  useSeo({
    title: "SimplyRefer | Mental Health Referrals for Australian GP Practices",
    description:
      "SimplyRefer helps Australian GP practices find psychologists and psychiatrists by specialty, expertise and availability, without changing the normal referral process.",
    canonicalPath: "/",
  });
  const { user, role, roleLoading } = useAuth();
  const navigate = useNavigate();
  const signedIn = !!user && !roleLoading;
  const firstName = user?.user_metadata?.first_name;

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5 sm:px-8">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <SRMark size="lg" />
            <span className="font-heading text-lg font-semibold tracking-tight text-slate-900">SimplyRefer</span>
          </Link>
          <nav className="hidden items-center gap-7 text-sm font-medium text-slate-600 md:flex">
            <Link to="/gp-practices" className="hover:text-primary">For GP Practices</Link>
            <Link to="/psychologists" className="hover:text-primary">Psychologists</Link>
            <Link to="/psychiatrists" className="hover:text-primary">Psychiatrists</Link>
            <Link to="/pricing" className="hover:text-primary">Pricing</Link>
            <Link to="/contact" className="hover:text-primary">Contact</Link>
          </nav>
          <div className="flex items-center gap-2">
            {signedIn ? (
              <>
                <span className="hidden text-sm font-medium text-slate-600 sm:inline" data-testid="landing-user-name">
                  {firstName ? `Hi, ${firstName}` : "My account"}
                </span>
                <Button size="sm" onClick={() => navigate(portalPath(role))} data-testid="landing-portal-button">
                  Go to my portal <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm" data-testid="landing-signin-button">
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button asChild size="sm" data-testid="landing-getstarted-button">
                  <Link to="/signup">Create account</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-16 sm:px-8 lg:grid-cols-2 lg:py-24">
          <div className="animate-fadein">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-secondary bg-secondary/60 px-3 py-1 text-xs font-semibold text-secondary-foreground">
              <ShieldCheck className="h-3.5 w-3.5" /> Every clinician is AHPRA-verified
            </span>
            <h1 className="mt-5 font-heading text-4xl font-semibold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Helping GP practices find the right available mental health clinician.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-500">
              SimplyRefer helps GP practices find psychologists and psychiatrists by specialty, expertise and
              availability, without changing the normal referral process.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" variant="brand" data-testid="hero-gp-cta">
                <Link to="/signup/gp">Get early GP practice access <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="brand-outline" data-testid="hero-directory-cta">
                <Link to="/clinician/signup">Join the directory</Link>
              </Button>
            </div>
            <p className="mt-4 text-sm text-slate-400">Free for GP practices · AHPRA-verified clinicians · No patient information stored.</p>
          </div>
          <div className="relative">
            <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-xl">
              <img src={HERO_IMG} alt="A GP and patient finishing a quick, efficient consultation at a clinic desk" className="h-[420px] w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto grid max-w-6xl gap-6 px-5 py-8 sm:grid-cols-3 sm:px-8">
          <Stat icon={ShieldCheck} title="AHPRA-verified" sub="Every clinician checked before listing" />
          <Stat icon={Gift} title="3 months free" sub="For clinics & individual practitioners" />
          <Stat icon={ShieldCheck} title="Private by design" sub="No patient information ever stored" />
        </div>
      </section>

      {/* Who it's for */}
      <section id="who" className="bg-slate-50">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">Who it's for</p>
            <h2 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-slate-900">Built for every side of the referral</h2>
          </div>
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            <AudienceCard
              testId="audience-gp" accent="bg-secondary text-secondary-foreground" icon={Stethoscope}
              eyebrow="For GPs · Free" title="Find clinicians, faster"
              points={["Search a verified directory", "See live availability and wait times", "Choose the referral option that suits your workflow"]}
              cta="Sign up free" ctaTo="/signup/gp" video="/videos/gp.mp4" videoTitle="How GPs use SimplyRefer"
            />
            <AudienceCard
              testId="audience-clinic" accent="bg-primary/10 text-primary" icon={Building2}
              eyebrow="For clinic managers" title="Manage your whole practice"
              points={["Add & manage your clinicians", "Manage their calendars & availability", "See top referring GP clinics"]}
              cta="Register your clinic" ctaTo="/clinic/signup" video="/videos/clinic.mp4" videoTitle="How clinic managers use SimplyRefer"
            />
            <AudienceCard
              testId="audience-clinician" accent="bg-emerald-50 text-emerald-700" icon={UserRound}
              eyebrow="For individual practitioners" title="Get referrals from GPs"
              points={["For solo clinicians who manage their own calendar", "Set your own availability anytime", "See which GP clinics refer to you"]}
              cta="Join the directory" ctaTo="/clinician/signup" video="/videos/clinician.mp4" videoTitle="How clinicians use SimplyRefer"
            />
          </div>
        </div>
      </section>

      {/* Always-accurate availability */}
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-16 sm:px-8 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-brand">Always-accurate availability</p>
            <h2 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-slate-900">GPs always see who's really taking patients</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-500">
              Each completed referral option shows clinicians that GPs are using their availability information and which practices are choosing them.
              That gives clinicians a direct incentive to keep their profile and availability current, so the
              directory GPs rely on stays accurate.
            </p>
            <div className="mt-6 flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <TrendingUp className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
              <p className="text-sm text-slate-600">
                Clinicians see an anonymous monthly tally of their top referring GP clinics — no patient data, just the practices sending work their way.
              </p>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-7 shadow-sm">
            <div className="flex items-center gap-2.5">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground">
                <CalendarClock className="h-5 w-5" />
              </div>
              <div>
                <p className="font-heading text-base font-semibold text-slate-900">Monthly availability check-in</p>
                <p className="text-sm text-slate-500">We prompt every clinician once a month to confirm their status:</p>
              </div>
            </div>
            <ul className="mt-5 space-y-2.5">
              {[
                "Taking patients — accepting new referrals now",
                "In 2 months",
                "Around 3 months wait",
                "Closed books — not accepting referrals",
              ].map((o) => (
                <li key={o} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> {o}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-xs text-slate-400">One click keeps their listing accurate — so GPs never refer to a full book.</p>
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <PricingTeaser />

      {/* How it works */}
      <section id="how" className="bg-slate-50">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">How it works</p>
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            <Step n="1" icon={Search} title="Search the directory" body="Filter AHPRA-verified clinicians by specialty, suburb and current availability — no more phone tag." />
            <Step n="2" icon={KeyRound} title="Simply Refer" body="Select the clinician, continue through your usual system, create an optional SimplyRefer Reference, or open a prefilled email. No patient health information is recorded." />
            <Step n="3" icon={BadgeCheck} title="Track referring clinics" body="Clinicians and clinics see which GP practices refer most — an anonymous monthly tally, completely private." />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-primary">
        <div className="mx-auto max-w-6xl px-5 py-16 text-center sm:px-8">
          <h2 className="font-heading text-3xl font-semibold tracking-tight text-white">Ready to make referrals simple?</h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-white/70">Join GPs and clinicians already connecting on SimplyRefer.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary" data-testid="final-gp-cta"><Link to="/signup/gp">Get started — GP</Link></Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white/10" data-testid="final-clinic-cta"><Link to="/clinic/signup">Register a clinic</Link></Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white/10" data-testid="final-clinician-cta"><Link to="/clinician/signup">Join as clinician</Link></Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <SRMark size="sm" />
              <span className="font-heading text-base font-semibold text-slate-900">SimplyRefer</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
              <Link to="/gp-practices" className="hover:text-primary">GP Practices</Link>
              <Link to="/psychologists" className="hover:text-primary">Psychologists</Link>
              <Link to="/psychiatrists" className="hover:text-primary">Psychiatrists</Link>
              <Link to="/pricing" className="hover:text-primary">Pricing</Link>
              <Link to="/privacy" className="hover:text-primary">Privacy</Link>
              <Link to="/terms" className="hover:text-primary">Terms</Link>
              <Link to="/contact" className="hover:text-primary">Contact</Link>
              <Link to="/login" className="hover:text-primary">Sign in</Link>
            </div>
          </div>
          <p className="mt-8 text-xs text-slate-400">© {new Date().getFullYear()} SimplyRefer. Making referrals simpler.</p>
        </div>
      </footer>
    </div>
  );
}
